import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from 'common/components/Button';
import { Link } from 'common/components/Link/animated';
import { IAnyType } from 'common/types/common';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

gsap.registerPlugin(ScrollTrigger);

export const Services: React.FC = () => {
  const { t } = useTranslation('pageServices');
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const b2bServices = t('b2bServices', { returnObjects: true }) as IAnyType[];
  const legacyServices = t('services', { returnObjects: true }) as IAnyType[];

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const syncBreakpoint = () => {
      setIsLargeScreen(mediaQuery.matches);
    };
    syncBreakpoint();
    mediaQuery.addEventListener('change', syncBreakpoint);
    return () => {
      mediaQuery.removeEventListener('change', syncBreakpoint);
    };
  }, []);

  useEffect(() => {
    if (!sectionRef.current || !b2bServices.length || !isLargeScreen) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray<HTMLElement>('[data-services-card]');

      cards.forEach((card, index) => {
        gsap.set(card, {
          transformOrigin: 'center top',
          zIndex: index + 1,
        });
      });

      cards.forEach((card, index) => {
        const currentPanel = document.querySelector(`#service-item-panel-${index + 1}`);
        gsap.fromTo(
          card,
          { scale: 1 },
          {
            scale: 0.96,
            ease: 'none',
            scrollTrigger: {
              trigger: currentPanel,
              start: 'top 10%',
              end: 'top 0%',
              scrub: true,
            },
          },
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [b2bServices, isLargeScreen]);

  return (
    <section ref={sectionRef} className="bg-gradient relative py-8 md:py-12 lg:py-16">
      <div className="container">
        <div className="mb-12 max-w-[900px]">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-primary" />
            <div className="font-semibold uppercase text-primary text-xs">
              {t('b2bSection.eyebrow')}
            </div>
          </div>
          <h2
            className="text-heading-about font-black text-white"
            dangerouslySetInnerHTML={{ __html: t('b2bSection.title') }}
          />
          <p className="mt-6 max-w-[64ch] text-white/78">{t('b2bSection.description')}</p>
        </div>

        <div className="relative isolate space-y-8 lg:space-y-10">
          {b2bServices.map((service: IAnyType, index: number) => (
            <article
              key={service.slug}
              id={`service-item-panel-${index + 1}`}
              data-services-card
              className="rounded-[30px] border border-white/10 bg-[#21172d] shadow-[0_24px_80px_rgba(0,0,0,0.22)] will-change-transform lg:sticky"
              style={{ zIndex: index + 1, top: isLargeScreen ? 85 : undefined }}>
              <div>
                <div data-services-card-content>
                  <div className="flex items-center justify-between gap-4 p-6 lg:px-10">
                    <div className="flex items-center gap-3">
                      <div className="absolute top-4 right-10 text-3xl font-semibold uppercase text-outline-primary text-heading-about">
                        0{index + 1}
                      </div>
                      <span className="h-px w-10 bg-primary" />
                      <h3
                        className="font-semibold text-primary text-2xl"
                        dangerouslySetInnerHTML={{ __html: service.title }}
                      />
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
                    <div className="flex flex-col justify-between px-6 pb-6 lg:px-10 lg:pb-10">
                      {!!service.image && (
                        <div className="flex min-h-[220px] flex-1 justify-center items-center rounded-[24px] border border-white/10 bg-[#140f1d] p-4 lg:min-h-[280px]">
                          <Image
                            alt={service.image.alt}
                            className="h-full max-h-[40vh] w-full object-contain object-top"
                            height={service.image.height}
                            loading="eager"
                            priority
                            src={service.image.src}
                            width={service.image.width}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex h-full flex-col p-6 pt-0 lg:px-10 lg:pb-10">
                      <div className="group relative rounded-[24px] p-6 md:col-span-2">
                        <p className="mt-3 max-w-[95%]">{service.description}</p>
                      </div>
                      <div className="grid gap-2 lg:gap-4 md:grid-cols-2">
                        {(service.highlights || []).map(
                          (item: IAnyType, highlightIndex: number) => (
                            <div
                              key={item.title}
                              className={clsx(
                                'group rounded-[24px] border border-white/10 bg-[#140f1d] p-6 relative',
                                {
                                  'col-span-2': highlightIndex === 0 || highlightIndex === 3,
                                },
                              )}>
                              <div className="text-outline-primary-light absolute top-3 right-6 text-2xl opacity-50 transition-all duration-500 group-hover:opacity-100">
                                {String(highlightIndex + 1).padStart(2, '0')}
                              </div>
                              <h4 className="text-2xl font-bold leading-tight text-primary max-w-[90%]">
                                {item.title}
                              </h4>
                              <p className="mt-3 max-w-[70ch]">{item.text}</p>
                            </div>
                          ),
                        )}
                      </div>
                      <div className="mt-8 flex w-full flex-col gap-2 sm:flex-row sm:justify-end sm:pr-4">
                        <Button href={service.slug} type="outline">
                          {t('pageServices:exploreService')}
                        </Button>
                        <Button href={service.slug}>{t('common:workWithUs')}</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="pt-20 md:pt-32">
          <div className="mb-8 max-w-[760px]">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-primary " />
              <div className="font-semibold uppercase text-primary text-xs">
                {t('legacySection.eyebrow')}
              </div>
            </div>
            <h2
              className="text-heading-about"
              dangerouslySetInnerHTML={{ __html: t('legacySection.title') }}
            />
            <p className="mt-4 max-w-[62ch]">{t('legacySection.description')}</p>
          </div>

          <div className="grid gap-2 lg:gap-4 lg:grid-cols-2">
            {legacyServices.map((service: IAnyType) => (
              <div
                key={service.title}
                className="rounded-[24px] border border-white/10 bg-white/[0.03] p-6 lg:p-8">
                <h3
                  className="text-heading-item mb-4 font-bold text-white"
                  dangerouslySetInnerHTML={{ __html: service.title }}
                />
                <div
                  className="max-w-[60ch]"
                  dangerouslySetInnerHTML={{ __html: service.content }}
                />
                {!!service.slug && (
                  <div className="mt-6">
                    <Link className="text-primary" href={service.slug}>
                      {t('common:readMore')}
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
